import { useRef, useState } from 'react'
import { useClickAway } from 'react-use'

import caretIcon from 'assets/images/explore-learnings/caret.svg'

interface SelectSingleProps {
  options?: { key: string; value: string }[]
  placeholder?: string
  selectValue: string
  onChange: (value: string) => void
  selectWidth?: string
  dropdownWidth?: string
  hasError?: boolean
  disabled?: boolean
}

export const SelectSingle = ({
  options,
  placeholder = 'Select options',
  selectValue,
  onChange: setSelectValue,
  selectWidth = '198px',
  dropdownWidth = '256px',
  hasError = false,
  disabled = false,
}: SelectSingleProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const selectedText = options?.find(option => option.value === selectValue)?.key || placeholder

  const onSelectedValue = (option: { key: string; value: string }) => {
    if (disabled) return
    setSelectValue(option.value)
    setOpen(false)
  }

  const selectRef = useRef<HTMLDivElement | null>(null)
  const uid = `select-single-${Math.random().toString(36).substring(2, 11)}`

  useClickAway(selectRef, (e: Event) => {
    if (disabled) return
    const selectedClasses = (e.target as HTMLElement | null)?.getAttribute('class')
    if (selectedClasses?.includes(uid)) return
    setOpen(false)
  })

  const handleToggle = () => {
    if (disabled) return
    setOpen(prev => !prev)
  }

  const isActive = !disabled && (open || selectValue.length > 0)
  const isFocused = !disabled && open
  const hasValue = !disabled && selectValue.length > 0

  return (
    <div className="relative" style={{ width: selectWidth }}>
      <div
        className={`
        py-2 sel px-[10px] border border-solid rounded-8 flex cursor-pointer text-neutral-70 border-neutral-60 ${uid}
        ${isActive ? 'text-black' : ''}
        ${isFocused ? 'border-black' : ''}
        ${disabled ? 'bg-[#F1F1F1]' : 'bg-white'}
        ${hasError && !disabled ? 'border-red-60' : ''}
      `}
        onClick={handleToggle}
      >
        <span
          className={`
                rounded-8 sel line-clamp-1 break-all w-full body-s ${uid}
                ${disabled ? 'text-[#9B9B9B]' : hasValue ? 'text-black' : 'text-[#6E6E6E]'}
              `}
        >
          {selectedText}
        </span>

        <img
          src={caretIcon}
          alt="Caret"
          className={`
            ml-2 transition-all ease-in-out
            ${open ? 'rotate-180' : ''}
            ${disabled ? 'opacity-50' : ''}
            ${uid}
          `}
        />
      </div>
      {!disabled && (
        <div
          className={`${open ? 'block' : 'hidden'} absolute mt-1 bg-white rounded-8 z-10 shadow-md`}
          style={{ width: dropdownWidth }}
          ref={selectRef}
        >
          <div className="max-h-40 overflow-y-auto p-2 scroller">
            {options &&
              options.map(option => (
                <div
                  key={option.key}
                  className={`flex items-center px-2 py-[6px] mb-1 body-s line-clamp-1 ${dropdownWidth} cursor-pointer 
                    ${selectedText === option.key ? 'text-black font-semibold' : 'text-[#505050]'}`}
                >
                  <li onClick={() => onSelectedValue(option)}>{option.key}</li>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
